import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { getCommonProducts, createCommonProduct, updateCommonProduct, deleteCommonProduct } from "../../services/commonProductService";
import { useNavigate, useLocation } from "react-router-dom";
import { getSubCategories } from "../../services/subCategoryService";

const CommonProductManagement = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortField, setSortField] = useState("name");
  const [sortValue, setSortValue] = useState("ASC");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [isActive, setIsActive] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [showUnitsSold, setShowUnitsSold] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subCategoryId: "",
    name: "",
    description: "",
    baseSku: "",
    brand: "",
    isActive: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const restoringRef = useRef(false);

  const queryClient = useQueryClient();
  // restore pagination/filter state when coming back from detail
  React.useEffect(() => {
    if (location.state?.fromProducts) {
      const { page: p, sortField: sf, sortValue: sv, subCategoryId: sc, isActive: ia, searchKey: sk } = location.state;
      if (p) setPage(p);
      if (sf) setSortField(sf);
      if (sv) setSortValue(sv);
      if (sc !== undefined) setSubCategoryId(sc);
      if (ia !== undefined) setIsActive(ia);
      if (sk !== undefined) setSearchKey(sk);
      restoringRef.current = true;
    }
  }, [location.state]);

  // Fetch sub-categories for dropdown
  const { data: subCategoriesData } = useQuery(
    ["subCategories"],
    () => getSubCategories({ page: 1, limit: 1000 }),
    { refetchOnWindowFocus: false }
  );

  const subCategories = subCategoriesData?.data?.data || [];

  const { data: productsData, isLoading, refetch } = useQuery(
    ["commonProducts", page, limit, sortField, sortValue, subCategoryId, isActive, searchKey, showUnitsSold],
    () => getCommonProducts({
      page,
      limit,
      sortField,
      sortValue,
      subCategoryId,
      isActive,
      searchKey,
      showUnitsSold: showUnitsSold
    }),
    {
      onError: (error) => {
        toast.error(error.message || "Failed to load products");
      },
      refetchOnWindowFocus: false,
    }
  );

  const createProductMutation = useMutation(
    (data) => createCommonProduct(data),
    {
      onSuccess: () => {
        toast.success("Product created successfully");
        queryClient.invalidateQueries("commonProducts");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to create product");
      },
    }
  );

  const updateProductMutation = useMutation(
    (data) => updateCommonProduct(data),
    {
      onSuccess: () => {
        toast.success("Product updated successfully");
        queryClient.invalidateQueries("commonProducts");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update product");
      },
    }
  );

  const deleteProductMutation = useMutation(
    (productId) => deleteCommonProduct(productId),
    {
      onSuccess: () => {
        toast.success("Product deleted successfully");
        queryClient.invalidateQueries("commonProducts");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to delete product");
      },
    }
  );

  // Extract data from server response
  const products = React.useMemo(() => {
    if (!productsData || !productsData.data) return [];
    return Array.isArray(productsData.data.data) ? productsData.data.data : [];
  }, [productsData]);

  const totalCount = productsData?.data?.count || 0;
  const hasNext = productsData?.data?.hasNext || false;
  const totalPages = Math.ceil(totalCount / limit);

  const resetForm = () => {
    setFormData({
      subCategoryId: "",
      name: "",
      description: "",
      baseSku: "",
      brand: "",
      isActive: true
    });
    setSelectedProduct(null);
  };

  const handleCreate = () => {
    setModalType("create");
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setModalType("edit");
    setSelectedProduct(product);
    setFormData({
      subCategoryId: product.subCategoryId?.toString() || "",
      name: product.name || "",
      description: product.description || "",
      baseSku: product.baseSku || "",
      brand: product.brand || "",
      isActive: product.isActive !== undefined ? product.isActive : true
    });
    setShowModal(true);
  };

  const handleDelete = (product) => {
    if (window.confirm(`Are you sure you want to delete product "${product.name}"?`)) {
      deleteProductMutation.mutate(product.id);
    }
  };

  const handleSelectDetail = (productId) => {
    navigate(`/products/common/${productId}`, {
      state: {
        fromProducts: true,
        tab: "products",
        page,
        sortField,
        sortValue,
        subCategoryId,
        isActive,
        searchKey,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        subCategoryId: formData.subCategoryId ? parseInt(formData.subCategoryId) : null
      };
      if (modalType === "create") {
        await createProductMutation.mutateAsync(submitData);
      } else {
        await updateProductMutation.mutateAsync({
          productId: selectedProduct.id,
          ...submitData
        });
      }
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortValue(sortValue === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortValue("ASC");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  React.useEffect(() => {
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    setPage(1);
  }, [searchKey, subCategoryId, isActive, sortField, sortValue]);

  const getSubCategoryName = (subCatId) => {
    const subCat = subCategories.find(sc => sc.id === subCatId);
    return subCat?.name || "Uncategorized";
  };

  return (
    <div >
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Management</h1>
        <p className="text-gray-600">Manage common products</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>

            <select
              value={subCategoryId}
              onChange={(e) => setSubCategoryId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Sub-Categories</option>
              {subCategories.map(subCat => (
                <option key={subCat.id} value={subCat.id}>{subCat.name}</option>
              ))}
            </select>

            <select
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUnitsSold}
                onChange={(e) => setShowUnitsSold(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show units sold</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FiRefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
            >
              <FiPlus size={16} className="mr-2" />
              Add Product
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center">
                    <span>Name</span>
                    {sortField === "name" && (
                      <span className="ml-2">
                        {sortValue === "ASC" ? (
                          <FiChevronUp className="inline" size={16} />
                        ) : (
                          <FiChevronDown className="inline" size={16} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sub-Category
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("baseSku")}
                >
                  <div className="flex items-center">
                    <span>SKU</span>
                    {sortField === "baseSku" && (
                      <span className="ml-2">
                        {sortValue === "ASC" ? (
                          <FiChevronUp className="inline" size={16} />
                        ) : (
                          <FiChevronDown className="inline" size={16} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("brand")}
                >
                  <div className="flex items-center">
                    <span>Brand</span>
                    {sortField === "brand" && (
                      <span className="ml-2">
                        {sortValue === "ASC" ? (
                          <FiChevronUp className="inline" size={16} />
                        ) : (
                          <FiChevronDown className="inline" size={16} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("isActive")}
                >
                  <div className="flex items-center">
                    <span>Status</span>
                    {sortField === "isActive" && (
                      <span className="ml-2">
                        {sortValue === "ASC" ? (
                          <FiChevronUp className="inline" size={16} />
                        ) : (
                          <FiChevronDown className="inline" size={16} />
                        )}
                      </span>
                    )}
                  </div>
                </th>
                {showUnitsSold && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units sold
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={showUnitsSold ? 7 : 6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={showUnitsSold ? 7 : 6} className="px-6 py-12 text-center text-gray-500">No products found</td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleSelectDetail(product.id)}
                        className="text-left text-sm font-medium text-blue-600 hover:underline"
                      >
                        {product.name}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getSubCategoryName(product.subCategoryId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.baseSku || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.brand || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {product.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {showUnitsSold && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {product.unitsSold != null ? product.unitsSold : "-"}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handleEdit(product)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded">
                          <FiEdit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(product)} className="text-red-600 hover:text-red-900 p-1 rounded">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
                  <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 10) {
                      pageNum = i + 1;
                    } else if (page <= 5) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 4) {
                      pageNum = totalPages - 9 + i;
                    } else {
                      pageNum = page - 5 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === page
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === "create" ? "Create Product" : "Edit Product"}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
                  <select
                    value={formData.subCategoryId}
                    onChange={(e) => setFormData({ ...formData, subCategoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Sub-Category (Optional)</option>
                    {subCategories.map(subCat => (
                      <option key={subCat.id} value={subCat.id}>{subCat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base SKU</label>
                  <input
                    type="text"
                    value={formData.baseSku}
                    onChange={(e) => setFormData({ ...formData, baseSku: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "true" })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                  >
                    {isSubmitting ? "Saving..." : modalType === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonProductManagement;

